// components/WhatsAppQuickButtons.js - Versión con detección automática de idioma
import React, { useState, useEffect } from 'react';
import { authService } from '../services/authService';

// 🆕 MAPEO DE IDs A NOMBRES DE TÉCNICOS
const tecnicoIds = {
  21936: 'Benito',
  23415: 'Pablo', 
  21945: 'Dani',
  31171: 'Ivan'
};

// 🆕 FUNCIÓN: Obtener nombre del técnico por ID
const getTechnicianNameById = (assigneeId, agentId) => {
  const id = assigneeId || agentId;
  return tecnicoIds[id] || 'Técnico asignado';
};

const WhatsAppQuickButtons = ({ orden, className = "" }) => {
  const [clienteInfo, setClienteInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clientesCache] = useState(new Map());
  const [isSpanishPhone, setIsSpanishPhone] = useState(true);
  
  const empresaNombre = 'Climatización Zapata';

  // Función para detectar si un teléfono es español
  const detectPhoneCountry = (phone) => {
    if (!phone) return true;
    
    const cleaned = phone.replace(/\D/g, '');
    const isSpanish = cleaned.startsWith('34') || (!cleaned.startsWith('34') && cleaned.length === 9);
    
    console.log(`📞 Quick buttons - Detectando país: ${phone} -> ${cleaned} -> Español: ${isSpanish}`);
    return isSpanish;
  };

  // Función para procesar información del cliente
  const procesarInformacionCliente = (clienteData) => {
    if (!clienteData) return null;

    let displayName = '';
    if (clienteData["legal-name"] && clienteData["legal-name"].trim()) {
      displayName = clienteData["legal-name"];
    } else if (clienteData.name && clienteData.name.trim()) {
      displayName = clienteData.name;
    } else if (clienteData["full-reference"]) {
      displayName = clienteData["full-reference"];
    } else {
      displayName = `Cliente ${clienteData.id}`;
    }

    const telefono = (clienteData.phone && clienteData.phone.trim()) ? clienteData.phone.trim() : 
                    (clienteData.phone2 && clienteData.phone2.trim()) ? clienteData.phone2.trim() : '';

    let direccionFormateada = '';
    if (clienteData["main-address"]) {
      const addr = clienteData["main-address"];
      const componentes = [];
      if (addr["address-data"]) componentes.push(addr["address-data"]);
      if (addr["city-town"]) componentes.push(addr["city-town"]);
      if (addr.province) componentes.push(addr.province);
      direccionFormateada = componentes.join(', ');
    }

    return {
      displayName,
      telefono,
      direccionFormateada
    };
  };

  // Función para cargar cliente
  const cargarCliente = async (accountPath) => {
    if (!accountPath) return;
    
    if (clientesCache.has(accountPath)) {
      const clienteCacheado = clientesCache.get(accountPath);
      setClienteInfo(clienteCacheado);
      setIsSpanishPhone(detectPhoneCountry(clienteCacheado.telefono));
      return;
    }

    setLoading(true);
    
    try {
      const url = `/.netlify/functions/getClient?accountPath=${encodeURIComponent(accountPath)}`;
      const response = await fetch(url, { headers: { ...authService.authHeader() } });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.client) {
          const clienteProcesado = procesarInformacionCliente(data.client);
          if (clienteProcesado) {
            clientesCache.set(accountPath, clienteProcesado);
            setClienteInfo(clienteProcesado);
            setIsSpanishPhone(detectPhoneCountry(clienteProcesado.telefono));
          }
        }
      }
    } catch (err) {
      console.error('Error cargando cliente para botones WhatsApp:', err);
    } finally {
      setLoading(false);
    }
  };

  // Cargar cliente al montar — la estimación de idioma con el teléfono que ya
  // trae la propia orden se hace aquí SIEMPRE (haya o no account-path), nunca
  // durante el render: antes, getClienteData() (llamada directamente en el
  // cuerpo del componente) llamaba a setIsSpanishPhone cada vez que
  // clienteInfo todavía era null — con muchas órdenes en la lista, eso
  // disparaba re-renders durante el propio render y React acababa lanzando
  // "Too many re-renders" (pantalla en blanco al abrir el día de un técnico).
  useEffect(() => {
    const accountPath = orden["account-path"];
    const basicPhone  = orden.customerPhone || orden['customer-phone'] || '';
    if (basicPhone) setIsSpanishPhone(detectPhoneCountry(basicPhone));
    if (accountPath) cargarCliente(accountPath);
  }, [orden]);

  // Obtener datos del cliente para WhatsApp — solo lectura, nunca actualiza estado
  const getClienteData = () => {
    if (clienteInfo) {
      return {
        nombre: clienteInfo.displayName,
        telefono: clienteInfo.telefono,
        direccion: clienteInfo.direccionFormateada
      };
    }

    const basicPhone = orden.customerPhone || orden['customer-phone'] || '';
    return {
      nombre: orden.customerName || orden['customer-name'] || (isSpanishPhone ? 'Cliente' : 'Customer'),
      telefono: basicPhone,
      direccion: orden.customerAddress || orden['customer-address'] || ''
    };
  };

  const cliente = getClienteData();
  // ✅ CORREGIDO: Obtener el nombre del técnico de forma más flexible
  const tecnico = orden.tecnico || 
                 orden.assignedTechnician || 
                 orden["assigned-technician"] ||
                 getTechnicianNameById(orden["assignee-id"], orden["agent-id"]);
                 
  // 🔍 DEBUG: Log para ver qué nombres estamos obteniendo
  console.log(`🔧 WhatsApp Quick Buttons - Orden ${orden.id}:`, {
    tecnicoOrden: orden.tecnico,
    assignedTechnician: orden.assignedTechnician,
    assigneeId: orden["assignee-id"],
    agentId: orden["agent-id"],
    tecnicoFinal: tecnico
  });
                 
  const fecha = new Date(orden.date).toLocaleDateString(isSpanishPhone ? 'es-ES' : 'en-US');
  const hora = orden.scheduledTime || '09:00';

  // Formatear número de teléfono
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    let cleaned = phone.replace(/\D/g, '');
    if (!cleaned.startsWith('34') && cleaned.length === 9) {
      cleaned = '34' + cleaned;
    }
    if (cleaned.startsWith('340')) {
      cleaned = '34' + cleaned.substring(3);
    }
    return '+' + cleaned;
  };

  // Generar mensajes bilingües
  const generateWhatsAppURL = (type) => {
    const phoneNumber = formatPhoneNumber(cliente.telefono);
    if (!phoneNumber) return '#';

    let message = '';
    
    if (isSpanishPhone) {
      switch (type) {
        case 'reminder':
          message = `🔧 ***Recordatorio de Cita - ${empresaNombre}***

¡Hola ${cliente.nombre}! 👋

Te recordamos que tienes una cita programada:

📅 ***Fecha:*** ${fecha}
👨‍🔧 ***Técnico:*** ${tecnico}${cliente.direccion ? `\n📍 ***Dirección:*** ${cliente.direccion}` : ''}

Si necesitas cambiar la cita, por favor contáctanos.

¡Nos vemos! 😊
https://climatizacionzapata.es/contacto/`;
          break;
        case 'onway':
          message = `🚗 *Tu técnico está en camino*\n\n¡Hola ${cliente.nombre}!\n\n${tecnico} está en camino a tu ubicación.\n⏱️ Tiempo estimado: 15-30 minutos\n\n¡Prepárate que ya llegamos! 🔧\n\n*${empresaNombre}*`;
          break;
        case 'completed':
          message = `✅ *Trabajo Completado*\n\n¡Hola ${cliente.nombre}!\n\n${tecnico} ha completado el trabajo.\n\nGracias por confiar en *${empresaNombre}*! 😊`;
          break;
        case 'confirmation':
          message = `📋 *Confirmación de Cita*\n\n¡Hola ${cliente.nombre}!\n\nConfirmamos tu cita:\n📅 ${fecha}\n⏰ ${tecnico}\n\n¿Estarás disponible? Por favor confirma respondiendo a este mensaje.\n\n*${empresaNombre}*`;
          break;
      }
    } else {
      switch (type) {
        case 'reminder':
          message = `🔧 ***Appointment Reminder - ${empresaNombre}***

Hello ${cliente.nombre}! 👋

We remind you that you have a scheduled appointment:

📅 ***Date:*** ${fecha}
👨‍🔧 ***Technician:*** ${tecnico}${cliente.direccion ? `\n📍 ***Address:*** ${cliente.direccion}` : ''}

If you need to change the appointment, please contact us.

See you! 😊
https://climatizacionzapata.es/contacto/`;
          break;
        case 'onway':
          message = `🚗 *Your technician is on the way*\n\nHello ${cliente.nombre}!\n\n${tecnico} is on the way to your location.\n⏱️ Estimated time: 15-30 minutes\n\nGet ready, we're almost there! 🔧\n\n*${empresaNombre}*`;
          break;
        case 'completed':
          message = `✅ *Work Completed*\n\nHello ${cliente.nombre}!\n\n${tecnico} has completed the work.\n\nThank you for trusting *${empresaNombre}*! 😊`;
          break;
        case 'confirmation':
          message = `📋 *Appointment Confirmation*\n\nHello ${cliente.nombre}!\n\nWe confirm your appointment:\n📅 ${fecha}\n⏰ ${tecnico}\n\nWill you be available? Please confirm by replying to this message.\n\n*${empresaNombre}*`;
          break;
      }
    }

    const encodedMessage = encodeURIComponent(message);
    return `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodedMessage}`;
  };

  // Abrir WhatsApp
  const openWhatsApp = (type, event) => {
    event.stopPropagation();
    const url = generateWhatsAppURL(type);
    if (url !== '#') {
      const idioma = isSpanishPhone ? '🇪🇸 ES' : '🌍 EN';
      console.log(`📱 Abriendo WhatsApp ${type} para ${cliente.nombre}: ${cliente.telefono} (${idioma})`);
      window.open(url, '_blank');
    }
  };

  const isValidPhone = cliente.telefono && cliente.telefono.replace(/\D/g, '').length >= 9;

  if (loading) {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        <div className="w-7 h-7 bg-gray-300 rounded text-xs flex items-center justify-center animate-pulse">
          ⏳
        </div>
      </div>
    );
  }

  if (!isValidPhone) {
    return (
      <div className={`text-xs text-gray-400 ${className}`} title={isSpanishPhone ? "Sin teléfono válido" : "No valid phone"}>
        📱❌
      </div>
    );
  }

  const titles = isSpanishPhone ? {
    reminder: `Enviar recordatorio de cita a ${cliente.nombre}`,
    onway: `Notificar que técnico está en camino a ${cliente.nombre}`,
    completed: `Notificar trabajo completado a ${cliente.nombre}`,
    confirmation: `Confirmar cita con ${cliente.nombre}`
  } : {
    reminder: `Send appointment reminder to ${cliente.nombre}`,
    onway: `Notify technician is on the way to ${cliente.nombre}`,
    completed: `Notify work completed to ${cliente.nombre}`,
    confirmation: `Confirm appointment with ${cliente.nombre}`
  };

  return (
    <div className={`flex gap-1 ${className}`}>
      <button
        onClick={(e) => openWhatsApp('reminder', e)}
        className="w-7 h-7 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs transition-colors shadow-sm flex items-center justify-center"
        title={titles.reminder + (isSpanishPhone ? ' 🇪🇸' : ' 🌍')}
      >
        📅
      </button>
      
      <button
        onClick={(e) => openWhatsApp('onway', e)}
        className="w-7 h-7 bg-orange-500 hover:bg-orange-600 text-white rounded text-xs transition-colors shadow-sm flex items-center justify-center"
        title={titles.onway + (isSpanishPhone ? ' 🇪🇸' : ' 🌍')}
      >
        🚗
      </button>
      
      <button
        onClick={(e) => openWhatsApp('completed', e)}
        className="w-7 h-7 bg-green-500 hover:bg-green-600 text-white rounded text-xs transition-colors shadow-sm flex items-center justify-center"
        title={titles.completed + (isSpanishPhone ? ' 🇪🇸' : ' 🌍')}
      >
        ✅
      </button>
      
      <button
        onClick={(e) => openWhatsApp('confirmation', e)}
        className="w-7 h-7 bg-purple-500 hover:bg-purple-600 text-white rounded text-xs transition-colors shadow-sm flex items-center justify-center"
        title={titles.confirmation + (isSpanishPhone ? ' 🇪🇸' : ' 🌍')}
      >
        📋
      </button>
    </div>
  );
};

// Componente para un solo botón de WhatsApp
export const WhatsAppSingleButton = ({ orden, type = 'reminder', children, className = "" }) => {
  const [clienteInfo, setClienteInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSpanishPhone, setIsSpanishPhone] = useState(true);
  
  const empresaNombre = 'Climatización Zapata';

  const detectPhoneCountry = (phone) => {
    if (!phone) return true;
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.startsWith('34') || (!cleaned.startsWith('34') && cleaned.length === 9);
  };

  const procesarCliente = (clienteData) => {
    if (!clienteData) return null;
    
    const displayName = clienteData["legal-name"] || clienteData.name || 
                       clienteData["full-reference"] || `Cliente ${clienteData.id}`;
    
    const telefono = clienteData.phone || clienteData.phone2 || '';
    
    return { displayName, telefono };
  };

  useEffect(() => {
    const cargarCliente = async () => {
      const accountPath = orden["account-path"];
      if (!accountPath) {
        const basicPhone = orden.customerPhone || orden['customer-phone'] || '';
        setIsSpanishPhone(detectPhoneCountry(basicPhone));
        return;
      }

      setLoading(true);
      try {
        const url = `/.netlify/functions/getClient?accountPath=${encodeURIComponent(accountPath)}`;
        const response = await fetch(url, { headers: { ...authService.authHeader() } });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.client) {
            const clienteProcesado = procesarCliente(data.client);
            if (clienteProcesado) {
              setClienteInfo(clienteProcesado);
              setIsSpanishPhone(detectPhoneCountry(clienteProcesado.telefono));
            }
          }
        }
      } catch (err) {
        console.error('Error cargando cliente para botón single:', err);
      } finally {
        setLoading(false);
      }
    };

    cargarCliente();
  }, [orden]);

  const cliente = clienteInfo || {
    displayName: orden.customerName || orden['customer-name'] || (isSpanishPhone ? 'Cliente' : 'Customer'),
    telefono: orden.customerPhone || orden['customer-phone'] || ''
  };

  // ✅ CORREGIDO: Obtener el nombre del técnico de forma más flexible
  const tecnico = orden.tecnico || 
                 orden.assignedTechnician || 
                 orden["assigned-technician"] ||
                 getTechnicianNameById(orden["assignee-id"], orden["agent-id"]);
  const fecha = new Date(orden.date).toLocaleDateString(isSpanishPhone ? 'es-ES' : 'en-US');
  const hora = orden.scheduledTime || '09:00';

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    let cleaned = phone.replace(/\D/g, '');
    if (!cleaned.startsWith('34') && cleaned.length === 9) {
      cleaned = '34' + cleaned;
    }
    return '+' + cleaned;
  };

  const generateMessage = () => {
    if (isSpanishPhone) {
      switch (type) {
        case 'reminder':
          return `🔧 ***Recordatorio de Cita - ${empresaNombre}***

¡Hola ${cliente.displayName}! 👋

Te recordamos que tienes una cita programada:

📅 ***Fecha:*** ${fecha}
👨‍🔧 ***Técnico:*** ${tecnico}

Si necesitas cambiar la cita, por favor contáctanos.

¡Nos vemos! 😊
https://climatizacionzapata.es/contacto/`;
        case 'onway':
          return `🚗 ¡Hola ${cliente.displayName}! ${tecnico} está en camino. Llegada estimada: 15-30 min. - ${empresaNombre}`;
        case 'completed':
          return `✅ ¡Hola ${cliente.displayName}! ${tecnico} ha completado el trabajo. ¡Gracias por confiar en ${empresaNombre}!`;
        case 'confirmation':
          return `📋 ¡Hola ${cliente.displayName}! Confirmamos cita el ${fecha} a las ${hora}. ¿Estarás disponible? - ${empresaNombre}`;
        default:
          return `¡Hola ${cliente.displayName}! - ${empresaNombre}`;
      }
    } else {
      switch (type) {
        case 'reminder':
          return `🔧 ***Appointment Reminder - ${empresaNombre}***

Hello ${cliente.displayName}! 👋

We remind you that you have a scheduled appointment:

📅 ***Date:*** ${fecha}
👨‍🔧 ***Technician:*** ${tecnico}

If you need to change the appointment, please contact us.

See you! 😊
https://climatizacionzapata.es/contacto/`;
        case 'onway':
          return `🚗 Hello ${cliente.displayName}! ${tecnico} is on the way. Estimated arrival: 15-30 min. - ${empresaNombre}`;
        case 'completed':
          return `✅ Hello ${cliente.displayName}! ${tecnico} has completed the work. Thank you for trusting ${empresaNombre}!`;
        case 'confirmation':
          return `📋 Hello ${cliente.displayName}! We confirm appointment on ${fecha} at ${hora}. Will you be available? - ${empresaNombre}`;
        default:
          return `Hello ${cliente.displayName}! - ${empresaNombre}`;
      }
    }
  };

  const openWhatsApp = (event) => {
    event.stopPropagation();
    const phoneNumber = formatPhoneNumber(cliente.telefono);
    if (!phoneNumber) return;
    
    const message = generateMessage();
    const url = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodeURIComponent(message)}`;
    const idioma = isSpanishPhone ? '🇪🇸 ES' : '🌍 EN';
    console.log(`📱 Abriendo WhatsApp single ${type} para ${cliente.displayName}: ${cliente.telefono} (${idioma})`);
    window.open(url, '_blank');
  };

  const isValidPhone = cliente.telefono && cliente.telefono.replace(/\D/g, '').length >= 9;

  if (loading) {
    return (
      <button
        className={`bg-gray-300 text-white rounded transition-colors shadow-sm flex items-center justify-center animate-pulse ${className}`}
        disabled
      >
        ⏳
      </button>
    );
  }

  if (!isValidPhone) {
    return null;
  }

  const title = isSpanishPhone ? 
    `Enviar WhatsApp ${type} a ${cliente.displayName} 🇪🇸` :
    `Send WhatsApp ${type} to ${cliente.displayName} 🌍`;

  return (
    <button
      onClick={openWhatsApp}
      className={`bg-green-500 hover:bg-green-600 text-white rounded transition-colors shadow-sm flex items-center justify-center ${className}`}
      title={title}
    >
      {children || '📱'}
    </button>
  );
};

export default WhatsAppQuickButtons;